import React, { useCallback, useMemo } from "react";
import { Row, Col } from "reactstrap";
import { useLoading } from "../../../../../../../hooks/useLoading";
import { IDnsEntriesResponse } from "../../../../../../../utils/fetch";
import LoadingSpinner from "../../../../../../../toolkit/loadingSpinner";
import ErrorAlert from "../../../../../../../toolkit/errorAlert";
import PrimaryButton from "../../../../../../../toolkit/primaryButton";
import WizardFooter from "../../../../../../../toolkit/wizardFooter";
import WizardFormStep from "../../../components/WizardFormStep";
import DnsManualRecordsTable from "../DnsManualRecordsTable";
import { formatDnsRecords } from "../../utils/formatDnsRecords";
import { useEntriModal, DnsEntriMode } from "../../hooks/useEntriModal";
import { DomainService } from "../../../../../../../services/domainService";
import IGlobalConfig from "../../../../../../../types/IGlobalConfig";
import {
  completeDnsDelegateSetup,
  DnsDelegateApiError,
  validateDnsDelegateDomain,
} from "../../../../../../../utils/dnsDelegateApi";
import * as yup from "yup";
import s from "../../dnsConfigurationStep/styles.scss";
import withStyles from "isomorphic-style-loader/withStyles";

export const dnsManualSetupSchema = yup.object({});

interface DnsSetupPanelProps {
  mode?: DnsEntriMode;
  domainData: IDnsEntriesResponse | null;
  isFetchingDns: boolean;
  dnsFetchError?: Error | null;
  config?: IGlobalConfig;
  adminUrl?: string;
  charityId?: string;
  serviceAccessToken?: string;
  delegateToken?: string;
  title?: string;
  subtitle?: string;
  onEntriAuthorizationSuccess?: () => void | Promise<void>;
  onManualValidateSuccess?: () => void | Promise<void>;
  onEntriAutomaticSuccess?: () => void | Promise<void>;
  showWizardFooter?: boolean;
  autoLaunchEntri?: boolean;
  className?: string;
}

const DnsSetupPanel: React.FC<DnsSetupPanelProps> = ({
  mode = "admin",
  domainData,
  isFetchingDns,
  dnsFetchError = null,
  config,
  adminUrl,
  charityId,
  serviceAccessToken,
  delegateToken,
  title = "Configure DNS Settings",
  subtitle = "Add these records to your domain provider or use automatic setup",
  onEntriAuthorizationSuccess,
  onManualValidateSuccess,
  onEntriAutomaticSuccess,
  showWizardFooter = true,
  autoLaunchEntri = true,
  className,
}) => {
  const resolvedAdminUrl = adminUrl || config?.adminServiceUrl;
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const useWizardChrome = mode === "admin" && showWizardFooter;

  const {
    loading: isEntriValidating,
    runWithLoading: runEntriDnsValidation,
  } = useLoading();

  const {
    loading: isManualValidating,
    runWithLoading: runManualDnsValidation,
  } = useLoading();

  const handleEntriAuthorization = useCallback(async () => {
    if (mode === "delegate" && resolvedAdminUrl && delegateToken) {
      await completeDnsDelegateSetup(resolvedAdminUrl, delegateToken);
      if (onEntriAutomaticSuccess) {
        await onEntriAutomaticSuccess();
      }
      return;
    }

    if (!onEntriAuthorizationSuccess) {
      return;
    }

    await runEntriDnsValidation(async () => {
      try {
        await onEntriAuthorizationSuccess();
      } catch {
        // parent handles waiting UI
      }
    }, 600);
  }, [
    mode,
    resolvedAdminUrl,
    delegateToken,
    onEntriAutomaticSuccess,
    onEntriAuthorizationSuccess,
    runEntriDnsValidation,
  ]);

  const { isLoadingEntri } = useEntriModal({
    domainData,
    isFetchingDns,
    adminUrl: resolvedAdminUrl,
    charityId,
    serviceAccessToken,
    delegateToken,
    mode,
    onAuthorizationSuccess: handleEntriAuthorization,
    onAutomaticEntriSuccess: handleEntriAuthorization,
    autoLaunch: autoLaunchEntri,
  });

  const records = useMemo(() => formatDnsRecords(domainData), [domainData]);

  const dnsFetchErrorMessage = useMemo(() => {
    if (!dnsFetchError) {
      return null;
    }
    if (dnsFetchError instanceof DnsDelegateApiError) {
      return dnsFetchError.message;
    }
    return dnsFetchError.message || "Failed to fetch DNS entries. Please try again or contact us for help.";
  }, [dnsFetchError]);

  const handleManualValidate = async () => {
    setErrorMessage(null);

    await runManualDnsValidation(async () => {
      if (mode === "delegate" && resolvedAdminUrl && delegateToken) {
        const validateDomainResponse = await validateDnsDelegateDomain(
          resolvedAdminUrl,
          delegateToken
        );

        if (!validateDomainResponse.valid) {
          setErrorMessage(
            validateDomainResponse.message ||
              "DNS verification failed. Please ensure your DNS records are correctly configured and try again."
          );
          throw new Error("DNS validation failed");
        }

        if (onManualValidateSuccess) {
          await onManualValidateSuccess();
        }
        return;
      }

      const validateDomainResponse = await DomainService.validateDomainDns(
        config,
        charityId,
        serviceAccessToken
      );

      if (!validateDomainResponse.valid) {
        setErrorMessage(
          validateDomainResponse.message ||
            "DNS verification failed. Please ensure your DNS records are correctly configured and try again."
        );
        throw new Error("DNS validation failed");
      }

      if (onManualValidateSuccess) {
        await onManualValidateSuccess();
      }
    }, 600);
  };

  if (dnsFetchErrorMessage && !isFetchingDns) {
    return (
      <Row className={mode === "delegate" ? "mt-2" : "mt-2 justify-content-center"}>
        <Col md={mode === "delegate" ? "12" : "9"}>
          <ErrorAlert message={dnsFetchErrorMessage} />
        </Col>
      </Row>
    );
  }

  if (isFetchingDns || isLoadingEntri) {
    return (
      <Row className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <LoadingSpinner
          title="Loading DNS configuration..."
          message="Please wait while we fetch your DNS entries."
        />
      </Row>
    );
  }

  if (!domainData) {
    return (
      <Row className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <LoadingSpinner
          title="Loading DNS configuration..."
          message="Please wait while we fetch your DNS entries."
        />
      </Row>
    );
  }

  if (isEntriValidating) {
    return (
      <Row className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <LoadingSpinner
          title="Verifying DNS configuration..."
          message="Please wait while we verify your DNS settings."
        />
      </Row>
    );
  }

  const content = (
    <>
      {errorMessage && (
        <Row className={mode === "delegate" ? "mt-2" : "mt-2 justify-content-center"}>
          <Col md={mode === "delegate" ? "12" : "9"}>
            <ErrorAlert message={errorMessage} />
          </Col>
        </Row>
      )}

      <DnsManualRecordsTable
        records={records}
        fullWidth={mode === "delegate"}
        domain={domainData?.domain}
      />

      {useWizardChrome ? (
        <WizardFooter
          schema={dnsManualSetupSchema}
          onNext={handleManualValidate}
          loading={isManualValidating}
          loadingText="Validating DNS entries..."
          loadingAnimation="pulse"
          finishButtonText="Submit"
        />
      ) : (
        showWizardFooter && (
          <Row className="mt-4 mb-1">
            <Col xs="12" className="d-flex justify-content-center">
              <PrimaryButton
                onClick={handleManualValidate}
                loading={isManualValidating}
                loadingText="Validating DNS entries..."
                loadingAnimation="pulse"
                variant="primary"
              >
                Submit
              </PrimaryButton>
            </Col>
          </Row>
        )
      )}
    </>
  );

  if (useWizardChrome) {
    return (
      <WizardFormStep
        title={title}
        subtitle={subtitle}
        className={className || s.dnsStepContainer}
      >
        {content}
      </WizardFormStep>
    );
  }

  return <div className={className}>{content}</div>;
};

export default withStyles(s)(DnsSetupPanel);
